import Icon from 'components/AppIcon';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';
import { useEffect, useMemo, useState } from 'react';

// 1. MOVE HELPER OUTSIDE to prevent focus loss
const InputField = ({
  label,
  name,
  placeholder,
  value,
  onChange,
  error,
  type = 'text',
  uppercase = false,
}) => (
  <div className="space-y-1.5">
    <label className="text-sm font-semibold text-gray-700">{label}</label>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl outline-none transition-all ${
        error
          ? 'border-red-500 focus:ring-2 focus:ring-red-200'
          : 'border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
      } ${uppercase ? 'uppercase' : ''}`}
    />
    {error && <p className="text-xs text-red-500 font-medium ml-1">{error}</p>}
  </div>
);

const AddBankDetailModal = ({ data, isOpen, onClose, onSubmit, onUpdate, isSubmitting }) => {
  const [formData, setFormData] = useState();
  const [isEditMode, setIsEditMode] = useState(false);
  const initialState = useMemo(() => {
    return {
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      branch_name: '',
      account_holder: '',
      account_type: 'SAVING',
    };
  }, []);
  useEffect(() => {
    if (data && isOpen) {
      setFormData(data);
      setIsEditMode(true);
    } else {
      setFormData(initialState);
      setIsEditMode(false);
    }
  }, [data, isOpen, initialState]);

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validate = () => {
    let newErrors = {};
    if (!formData.bank_name.trim()) newErrors.bank_name = 'Required';
    if (!/^\d{9,18}$/.test(formData.account_number))
      newErrors.account_number = '9-18 digits required';
    if (!/^[A-Za-z0-9]+$/.test(formData.ifsc_code.toUpperCase()))
      newErrors.ifsc_code = 'Invalid IFSC';
    if (!formData.account_holder.trim()) newErrors.account_holder = 'Required';
    if (!formData.branch_name.trim()) newErrors.branch_name = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update data
    setFormData((prev) => ({ ...prev, [name]: value }));

    // 2. Clear ONLY the error for the current field being typed in
    if (errors[name]) {
      setErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      if (isEditMode) {
        onUpdate?.(formData);
      } else {
        onSubmit?.(formData);
      }
    }
  };

  const handleClose = () => {
    setFormData(initialState);
    setIsEditMode(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
              <Icon name="Landmark" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isEditMode ? 'Edit Bank Detail' : 'Add Bank Detail'}
              </h1>
              <p className="text-sm text-gray-500">Ensure details match the bank passbook</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-200 text-gray-400 transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <InputField
                label="Bank Name"
                name="bank_name"
                placeholder="eg. HDFC Bank"
                value={formData.bank_name}
                onChange={handleInputChange}
                error={errors.bank_name}
              />
              <InputField
                label="Account Number"
                name="account_number"
                placeholder="eg. 501000..."
                value={formData.account_number}
                onChange={handleInputChange}
                error={errors.account_number}
                type="number"
              />
              <InputField
                label="IFSC Code"
                name="ifsc_code"
                placeholder="eg. HDFC0001234"
                uppercase={true}
                value={formData.ifsc_code}
                onChange={handleInputChange}
                error={errors.ifsc_code}
              />
              <InputField
                label="Branch Name"
                name="branch_name"
                placeholder="eg. Downtown Branch"
                value={formData.branch_name}
                onChange={handleInputChange}
                error={errors.branch_name}
              />
              <InputField
                label="Account Holder Name"
                name="account_holder"
                placeholder="Name on passbook"
                value={formData.account_holder}
                onChange={handleInputChange}
                error={errors.account_holder}
              />

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">Account Type</label>
                <select
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none appearance-none cursor-pointer"
                >
                  <option value="SAVING">Savings Account</option>
                  <option value="CURRENT">Current Account</option>
                </select>
              </div>
            </div>
          </div>

          <div className="px-8 py-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-xl text-sm font-semibold shadow-lg shadow-primary-200 transition-all flex items-center gap-2"
            >
              {isSubmitting ? <ThreeDotLoading /> : isEditMode ? 'Update Details' : 'Save Details'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBankDetailModal;
